import { Injectable } from '@angular/core';
import { BecomeInstructor, ForgotPassword, ResetPassword, Signup } from '@shared/models';
import { AuthInfo } from '@shared/store';
import {
  API_CLOSE_ACCOUNT,
  API_POST_SOCIAL_AUTH,
  API_RESET_PASSWORD_GET_USER,
  API_REVERIFY_ACCOUNT,
  API_USERS_AUTH_INFO,
  API_USERS_AUTH_SETTINGS_INFO,
  API_USERS_BECOMEINSTRUCTOR,
  API_USERS_DETAIL,
  API_USERS_FORGOT_PASSWORD,
  API_USERS_LOGIN,
  API_USERS_RESET_PASSWORD,
  API_USERS_RESET_PASSWORD_LINK,
  API_USERS_SET_PASSWORD,
  API_USERS_SIGNUP,
  API_USERS_UPDATE_ME,
  API_USERS_UPDATE_ME_EMAIL,
  API_USERS_UPDATE_PASSWORD,
  API_USERS_VERIFY_LINK_TOKEN,
} from 'projects/thkee-common/src/lib/models/constants';
import { BehaviorSubject, Observable, from, map, of, switchMap } from 'rxjs';
import {
  AuthSettingsInfo,
  CloseAccount,
  Credentials,
  CredentialsService,
  GoogleLoginResponse,
  HttpService,
  LoaderService,
  LoginContext,
  LoginResponse,
  STORAGE_CREDENTIALS_FIELD,
  SetEmailPayload,
  SetPasswordPayload,
  ToastService,
  UpdatePasswordPayload,
  UpdateRes,
  User,
  hashPassword,
} from 'thkee-common';

import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import Cookies from 'js-cookie';
import * as LogRocket from 'logrocket';
import { createPreAPIHook } from 'projects/thkee-marketplace-app/src/config-token';
import Session from 'supertokens-web-js/recipe/session';
import { GeolocationService } from '../public/geolocation.service';

interface LoginActionState {
  openLoginModal?: boolean;
  redirect?: string;
}

/**
 * Provides a base for authentication workflow.
 * The login/logout methods should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  loginActionState = new BehaviorSubject<LoginActionState>({});

  constructor(
    private credentialsService: CredentialsService,
    private readonly http: HttpService,
    private loaderService: LoaderService,
    private router: Router,
    private toastService: ToastService,
    private store: Store,
    private geoService: GeolocationService
  ) {}

  /**
   * Authenticates the user.
   * @param context The login parameters.
   * @return The user credentials.
   */
  login(context: LoginContext, trackerId?: string): Observable<Credentials> {
    return this.http
      .post<LoginResponse, LoginContext>(
        API_USERS_LOGIN,
        { email: context.email, password: hashPassword(context.password, context.email) },
        undefined,
        { trackerId: trackerId }
      )
      .pipe(
        map((res) => {
          LogRocket.startNewSession();
          const credentials: Credentials = {
            refresh: res.refresh,
            token: res.access,
          };
          this.credentialsService.setCredentials(credentials, context.remember);
          return credentials;
        })
      );
  }

  /**
   * Logs out the user and clear credentials.
   * @return True if the user was logged out successfully.
   */
  // logout(): Observable<boolean> {
  //   LogRocket.startNewSession();
  //   this.http.post(API_USERS_LOGOUT, {}).subscribe();
  //   this.credentialsService.setCredentials();

  //   // For Sign In As Admin
  //   const sectionStorageCredential = sessionStorage.getItem(STORAGE_CREDENTIALS_FIELD);
  //   if (sectionStorageCredential) {
  //     sessionStorage.removeItem(STORAGE_CREDENTIALS_FIELD);
  //   }
  //   return of(true);
  // }

  signup(
    signupData: { email: string; password: string; name: string; geo?: Partial<Geolocation> },
    options?: { contextId?: string }
  ): Observable<void> {
    const { email, password, name, geo } = signupData;
    const [firstName, ...lastNames] = name.split(/\s+/);
    const data: Signup = {
      first_name: firstName,
      last_name: lastNames.join(' '),
      email,
      username: email,
      password: hashPassword(password, email),
      ...geo,
    };

    LogRocket.startNewSession();
    return this.http.post<void>(API_USERS_SIGNUP, data, undefined, { trackerId: options?.contextId });
  }

  forgotPassword(data: ForgotPassword): Observable<void> {
    return this.http.post<void>(API_USERS_FORGOT_PASSWORD, data);
  }

  updateUser(user: Partial<User>, options?: { contextId?: string }): Observable<User> {
    return this.http.put<User>(API_USERS_UPDATE_ME, user, undefined, { trackerId: options?.contextId });
  }

  verifyEmailToken(action: string, token: string): Observable<void> {
    // return of(undefined); // fake success action
    return this.http.put(API_USERS_VERIFY_LINK_TOKEN, { action, token });
  }

  getUserByResetPasswordToken(code: string): Observable<User> {
    // return of({ first_name: 'Hai Le', email: 'haile.vnm@gmail.com' } as any)
    return this.http.post<User>(API_RESET_PASSWORD_GET_USER, { code });
  }

  reverifyEmail(trackerId?: string) {
    return this.http.post(API_REVERIFY_ACCOUNT, undefined, undefined, { trackerId: trackerId });
  }

  // NOTE: This method is for sing-in with google to get the CODE to send BE validation
  // which is not using for now
  // Login with google
  // signInWithGooglePopup() {
  //   // Initialize the Google OAuth 2.0 flow for a popup
  //   const client = window.google.accounts.oauth2.initCodeClient({
  //     client_id: environment.googleClientId, // Your Google Client ID
  //     scope: 'openid email profile', // Ask for email, profile, and OpenID (JWT)
  //     ux_mode: 'popup', // Use popup for login
  //     response_type: 'token id_token',
  //     callback: (response: any) => this.handleGoogleLoginResponse(response), // Handle response
  //   });

  //   // Trigger the login flow
  //   client.requestCode(); // Ensure this method is called to initiate the OAuth flow
  // }

  /**
   * Handle Google login response (ID token).
   */
  // handleGoogleLoginResponse(response: any) {
  //   const code = response.code; // This is the JWT token (ID token)
  //   if (code) {
  //     const token = { authorization_code: code };
  //     this.http.post<GoogleLoginResponse>(API_POST_SOCIAL_AUTH, token).subscribe({
  //       next: (res) => {
  //         const credentials: Credentials = {
  //           refresh: res.refresh_token,
  //           token: res.access_token,
  //         };
  //         this.credentialsService.setCredentials(credentials, true);
  //         this.store.dispatch(AuthActions.loginSuccess());
  //         this.router.navigate(['/student/account/profile']);
  //       },
  //       error: (error) => {
  //         this.toastService.error({
  //           message: $localize`Google login failed. Please try with your email!`,
  //         });
  //       },
  //     });
  //   } else {
  //     this.toastService.error({
  //       message: $localize`No ID token received!`,
  //     });
  //   }
  // }

  // NOTE: Using this method now
  singInWIthGoogle(idToken: string) {
    const token = { id_token: idToken };
    return this.http.post<GoogleLoginResponse>(API_POST_SOCIAL_AUTH, token).pipe(
      map((res) => {
        const credentials: Credentials = {
          refresh: res.refresh,
          token: res.access,
          signup: res.signup,
        };
        this.credentialsService.setCredentials(credentials, true);
        return credentials;
      })
    );
  }

  // Login As
  loginAsAdmin(credential: Credentials): Observable<Credentials> {
    const isCredential = localStorage.getItem(STORAGE_CREDENTIALS_FIELD);
    if (isCredential) {
      localStorage.removeItem(STORAGE_CREDENTIALS_FIELD);
      Cookies.remove(STORAGE_CREDENTIALS_FIELD);
    }
    const credentials: Credentials = {
      refresh: credential.refresh,
      token: credential.token,
    };
    // this.credentialsService.setCredentials(credentials, true);

    // For Sign In As Admin
    sessionStorage.setItem(STORAGE_CREDENTIALS_FIELD, JSON.stringify(credentials));

    return of(credentials);
  }

  closeAccount(data: CloseAccount): Observable<void> {
    return this.http.post<void>(API_CLOSE_ACCOUNT, data);
  }

  // New Auth with supper token
  getAuthSettingsInfo(): Observable<AuthSettingsInfo> {
    return this.http.get<AuthSettingsInfo>(API_USERS_AUTH_SETTINGS_INFO);
  }

  setNewPassword(payload: SetPasswordPayload) {
    return this.http.post<AuthSettingsInfo>(API_USERS_SET_PASSWORD, payload);
  }

  updateEmail(payload: SetEmailPayload): Observable<UpdateRes> {
    return this.http.post(API_USERS_UPDATE_ME_EMAIL, payload);
  }

  updatePassword(payload: UpdatePasswordPayload) {
    return this.http.post(API_USERS_UPDATE_PASSWORD, payload);
  }

  sentResetPasswordLink(): Observable<void> {
    return this.http.post<void>(API_USERS_RESET_PASSWORD_LINK, {});
  }

  getAuthInfo(): Observable<AuthInfo> {
    return this.http.get<AuthInfo>(API_USERS_AUTH_INFO);
  }

  resetPassword(data: ResetPassword) {
    return this.http.post(API_USERS_RESET_PASSWORD, data);
  }

  getUser(): Observable<User> {
    return from(Session.doesSessionExist()).pipe(
      switchMap((exists) => {
        if (exists) {
          return this.http.get<User>(API_USERS_DETAIL);
        } else {
          return of();
        }
      })
    );
  }

  signupInstructor(user: number, other: any): Observable<void> {
    const data: BecomeInstructor = {
      user: user,
      instructor_consent: other,
    };
    return this.http.post<void>(API_USERS_BECOMEINSTRUCTOR, data);
  }

  async logout() {
    await Session.signOut();
    LogRocket.startNewSession();
    localStorage.clear();
    Cookies.remove('st-last-access-token-update');
    Cookies.remove('thkee_anonymous_session');
    // Object.keys(Cookies.get()).forEach(function(cookieName) {
    //   Cookies.remove(cookieName);
    // });
    window.location.replace('/');
  }

  sessionExist(): Observable<boolean> {
    return from(Session.doesSessionExist()).pipe(
      switchMap((exists) => {
        return of(exists);
      })
    );
  }

  async isLogged(): Promise<boolean> {
    return Session.doesSessionExist();
  }

  getToken() {
    return from(Session.getAccessToken()).pipe(
      switchMap((token) => {
        return of(token);
      })
    );
  }

  async getAccessTokenPayloadSecurely() {
    return Session.getAccessTokenPayloadSecurely();
  }

  // Supper token UI(Login/Signup)
  async initSuperTokensUI(language: 'en' | 'ar' = 'ar') {
    await this.initGeolocation();
    const getCountryHeader = () => this.getCountryHeader();
    const preAPIHook = createPreAPIHook(getCountryHeader);
    (window as any).supertokensUIInit('supertokensui', {
      languageTranslations: {
        translations: this.authInfoTranslator(language),
        defaultLanguage: language,
      },
      appInfo: {
        websiteDomain: environment.homeUrlSSL,
        apiDomain: environment.serverUrl,
        appName: 'Thkee',
        apiBasePath: '/auth',
        websiteBasePath: this.websiteBasePath(language, environment.isLocalDev),
        // websiteBasePath: language === 'en' ? '/en/auth' : '/auth',
      },
      style: this.authInfoStyle(language),
      // useShadowDom: false,
      // Email & Password
      recipeList: [
        // (window as any).supertokensUIEmailPassword.init({
        //   signInAndUpFeature: {
        //     signUpForm: {
        //       formFields: [
        //         {
        //           id: 'first_name',
        //           label: 'First Name',
        //           placeholder: 'First Name',
        //         },
        //         {
        //           id: 'last_name',
        //           label: 'Last Name',
        //           placeholder: 'Last Name',
        //         },
        //       ],
        //     },
        //   },
        //   onHandleEvent: async (context: {
        //     action: string;
        //     isNewRecipeUser: any;
        //     user: { loginMethods: string | any[] };
        //   }) => {
        //     if (context.action === 'SUCCESS') {
        //       if (context.isNewRecipeUser && context.user.loginMethods.length === 1) {
        //         // New user sign up
        //         window.location.replace('/auth');
        //       } else {
        //         // Existing user sign in
        //       }
        //     }
        //   },
        // }),
        // (window as any).supertokensUIEmailPassword.init({
        //   resetPasswordUsingTokenFeature: {
        //     disableDefaultUI: true,
        //   },
        // }),
        // (window as any).supertokensUIPasswordless.init({
        //   contactMethod: 'EMAIL_OR_PHONE',
        // }),
        // Password less login
        (window as any).supertokensUIPasswordless.init({
          contactMethod: 'EMAIL_OR_PHONE', // You can also use "EMAIL" or "PHONE" based on your needs
          preAPIHook, // For x-country header
          onHandleEvent: async (context: {
            action: 'SUCCESS' | 'PASSWORDLESS_RESTART_FLOW' | 'PASSWORDLESS_CODE_SENT';
            isNewRecipeUser?: boolean;
            user?: {
              id: string;
              emails: string[];
              phoneNumbers?: string[];
              loginMethods: Array<{
                recipeId: string;
                timeJoined: number;
              }>;
            };
          }) => {
            if (context.action === 'PASSWORDLESS_RESTART_FLOW') {
              // User has restarted the passwordless flow
              // You can trigger Angular router navigation or other Angular-specific code here
            } else if (context.action === 'PASSWORDLESS_CODE_SENT') {
              // Code has been sent to the user
              // You might want to show a notification using Angular material or other UI components
            } else if (context.action === 'SUCCESS') {
              // Check if user exists before accessing its properties
              if (context.user) {
                const { id, emails, phoneNumbers } = context.user;

                if (context.isNewRecipeUser && context.user.loginMethods.length === 1) {
                  // New user sign up
                } else {
                  // Existing user sign in
                }
              } else {
                console.error('SUCCESS action received but user object is undefined');
              }
            }
          },
        }),
        (window as any).supertokensUIThirdParty.init({
          signInAndUpFeature: {
            preAPIHook, // For x-country header
            providers: [
              // (window as any).supertokensUIThirdParty.Github.init(),
              (window as any).supertokensUIThirdParty.Google.init(),
              // (window as any).supertokensUIThirdParty.Facebook.init(),
              // (window as any).supertokensUIThirdParty.Apple.init(),
            ],
          },
        }),
        (window as any).supertokensUISession.init({
          sessionTokenFrontendDomain: environment.thkCookieDomain,
        }),
        (window as any).supertokensUIEmailVerification.init({
          mode: 'REQUIRED', // or "OPTIONAL"
        }),
      ],
    });
  }

  authInfoTranslator(language: 'en' | 'ar' = 'ar') {
    let translations = {};
    if (language === 'ar') {
      translations = {
        ar: {
          // Common Strings
          AUTH_PAGE_HEADER_TITLE_SIGN_IN_AND_UP: 'تسجيل الدخول / إنشاء حساب',
          AUTH_PAGE_HEADER_TITLE_SIGN_IN: 'تسجيل الدخول',
          AUTH_PAGE_HEADER_TITLE_SIGN_UP: 'إنشاء حساب',
          AUTH_PAGE_HEADER_TITLE_SIGN_IN_UP_TO_APP: ' للمتابعة إلى ',
          AUTH_PAGE_HEADER_SUBTITLE_SIGN_IN_START: 'غير مسجل بعد؟',
          AUTH_PAGE_HEADER_SUBTITLE_SIGN_IN_SIGN_UP_LINK: 'إنشاء حساب',
          AUTH_PAGE_HEADER_SUBTITLE_SIGN_IN_END: '',
          AUTH_PAGE_HEADER_SUBTITLE_SIGN_UP_START: 'هل لديك حساب بالفعل؟',
          AUTH_PAGE_HEADER_SUBTITLE_SIGN_UP_SIGN_IN_LINK: 'تسجيل الدخول',
          AUTH_PAGE_HEADER_SUBTITLE_SIGN_UP_END: '',
          AUTH_PAGE_FOOTER_START: 'بالمتابعة، فإنك توافق على ',
          AUTH_PAGE_FOOTER_TOS: 'شروط الخدمة',
          AUTH_PAGE_FOOTER_AND: ' و ',
          AUTH_PAGE_FOOTER_PP: 'سياسة الخصوصية',
          AUTH_PAGE_FOOTER_END: '',
          DIVIDER_OR: 'أو',
          BRANDING_POWERED_BY_START: 'Powered by ',
          BRANDING_POWERED_BY_END: 'Thkee',
          SOMETHING_WENT_WRONG_ERROR: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
          SOMETHING_WENT_WRONG_ERROR_RELOAD: 'حدث خطأ ما. يرجى المحاولة لاحقًا أو إعادة تحميل الصفحة.',

          // Passwordless Translations
          GENERAL_ERROR_EMAIL_UNDEFINED: 'يرجى تحديد البريد الإلكتروني',
          GENERAL_ERROR_EMAIL_NON_STRING: 'يجب أن يكون البريد الإلكتروني من نوع نصي',
          GENERAL_ERROR_EMAIL_INVALID: 'البريد الإلكتروني غير صالح',
          GENERAL_ERROR_PHONE_UNDEFINED: 'يرجى تحديد رقم الهاتف',
          GENERAL_ERROR_PHONE_NON_STRING: 'يجب أن يكون رقم الهاتف من نوع نصي',
          GENERAL_ERROR_PHONE_INVALID: 'رقم الهاتف غير صالح',
          GENERAL_ERROR_OTP_UNDEFINED: 'يرجى ملء رمز التحقق',
          GENERAL_ERROR_OTP_INVALID: 'رمز تحقق غير صالح',
          GENERAL_ERROR_OTP_EXPIRED: 'انتهت صلاحية رمز التحقق.',
          GENERAL_ERROR_OTP_NON_STRING: 'يجب أن يكون رمز التحقق من نوع نصي',
          GENERAL_ERROR_OTP_EMPTY: 'لا يمكن أن يكون رمز التحقق فارغًا',
          ERROR_SIGN_IN_UP_LINK: 'رابط سحري غير صالح. يرجى المحاولة مرة أخرى.',
          ERROR_SIGN_IN_UP_RESEND_RESTART_FLOW: 'انتهت مهلة تسجيل الدخول. يرجى المحاولة مرة أخرى.',
          ERROR_SIGN_IN_UP_CODE_CONSUME_RESTART_FLOW: 'فشل تسجيل الدخول. يرجى المحاولة مرة أخرى.',
          PWLESS_SIGN_IN_UP_EMAIL_LABEL: 'البريد الإلكتروني',
          PWLESS_SIGN_IN_UP_PHONE_LABEL: 'رقم الهاتف',
          PWLESS_SIGN_IN_UP_SWITCH_TO_PHONE: 'استخدام رقم الهاتف',
          PWLESS_SIGN_IN_UP_SWITCH_TO_EMAIL: 'استخدام البريد الإلكتروني',
          PWLESS_SIGN_IN_UP_CONTINUE_BUTTON: 'متابعة',
          PWLESS_COMBO_CONTINUE_WITH_PASSWORDLESS_LINK: 'المتابعة بدون كلمة مرور',
          PWLESS_COMBO_CONTINUE_WITH_PASSWORDLESS_BUTTON: 'المتابعة بدون كلمة مرور',
          PWLESS_COMBO_PASSWORD_LABEL: 'كلمة المرور',
          PWLESS_COMBO_FORGOT_PW_LINK: 'نسيت كلمة المرور؟',
          PWLESS_LINK_SENT_RESEND_SUCCESS: 'تم إعادة إرسال الرابط',
          PWLESS_LINK_SENT_RESEND_TITLE: 'تم إرسال الرابط!',
          PWLESS_LINK_SENT_RESEND_DESC_START_EMAIL: 'لقد أرسلنا رابط إلى ',
          PWLESS_LINK_SENT_RESEND_DESC_START_PHONE: 'لقد أرسلنا رابط إلى رقم هاتفك ',
          PWLESS_LINK_SENT_RESEND_DESC_END_EMAIL: '. انقر على الرابط لتسجيل الدخول أو إنشاء حساب',
          PWLESS_LINK_SENT_RESEND_DESC_END_PHONE: '',
          PWLESS_SIGN_IN_UP_CHANGE_CONTACT_INFO_EMAIL: 'تغيير البريد الإلكتروني',
          PWLESS_SIGN_IN_UP_CHANGE_CONTACT_INFO_PHONE: 'تغيير رقم الهاتف',
          PWLESS_LINK_CLICKED_CONTINUE_HEADER: 'تسجيل الدخول أو إنشاء حساب',
          PWLESS_LINK_CLICKED_CONTINUE_DESC: 'انقر على الزر أدناه لتسجيل الدخول على هذا الجهاز',
          PWLESS_LINK_CLICKED_CONTINUE_BUTTON: 'متابعة',
          PWLESS_RESEND_SUCCESS_EMAIL: 'تم إعادة إرسال البريد الإلكتروني',
          PWLESS_RESEND_SUCCESS_PHONE: 'تم إعادة إرسال الرسالة القصيرة',
          PWLESS_RESEND_BTN_DISABLED_START: 'إعادة الإرسال خلال ',
          PWLESS_RESEND_BTN_DISABLED_END: '',
          PWLESS_RESEND_BTN_EMAIL: 'إعادة إرسال البريد الإلكتروني',
          PWLESS_RESEND_BTN_PHONE: 'إعادة إرسال الرسالة القصيرة',
          PWLESS_USER_INPUT_CODE_HEADER_TITLE: 'أدخل رمز التحقق',
          PWLESS_USER_INPUT_CODE_HEADER_SUBTITLE: 'تم إرسال رمز تحقق إليك على',
          PWLESS_USER_INPUT_CODE_HEADER_SUBTITLE_LINK: 'تم إرسال رمز تحقق ورابط سحري إليك على',
          PWLESS_USER_INPUT_CODE_INPUT_LABEL: 'رمز التحقق',
          PWLESS_MFA_HEADER_TITLE_PHONE: 'رمز تحقق عبر الرسائل القصيرة',
          PWLESS_MFA_HEADER_TITLE_EMAIL: 'رمز تحقق عبر البريد الإلكتروني',
          PWLESS_MFA_FOOTER_LOGOUT: 'تسجيل الخروج',

          // Third-Party Translations
          THIRD_PARTY_PROVIDER_DEFAULT_BTN_START: 'المتابعة مع ',
          THIRD_PARTY_PROVIDER_DEFAULT_BTN_END: '',
          THIRD_PARTY_ERROR_NO_EMAIL: 'تعذر استرداد البريد الإلكتروني. يرجى تجربة طريقة مختلفة.',
          'Cannot sign in / up due to security reasons. Please try a different login method or contact support. (ERR_CODE_004)':
            'لا يمكن تسجيل الدخول أو إنشاء الحساب لأسباب أمنية. يرجى تجربة طريقة تسجيل دخول مختلفة أو الاتصال بالدعم.',
          'Cannot sign in / up because new email cannot be applied to existing account. Please contact support. (ERR_CODE_005)':
            'لا يمكن تسجيل الدخول أو إنشاء الحساب لأن البريد الإلكتروني الجديد لا يمكن تطبيقه على الحساب الحالي. يرجى الاتصال بالدعم.',
          'Cannot sign in / up due to security reasons. Please try a different login method or contact support. (ERR_CODE_006)':
            'لا يمكن تسجيل الدخول أو إنشاء الحساب لأسباب أمنية. يرجى تجربة طريقة تسجيل دخول مختلفة أو الاتصال بالدعم.',
          'Cannot sign in / up due to security reasons. Please contact support. (ERR_CODE_017)':
            'لا يمكن تسجيل الدخول أو إنشاء الحساب لأسباب أمنية. يرجى الاتصال بالدعم.',
          'Cannot sign in / up due to security reasons. Please contact support. (ERR_CODE_018)':
            'لا يمكن تسجيل الدخول أو إنشاء الحساب لأسباب أمنية. يرجى الاتصال بالدعم.',
          'Cannot sign in / up due to security reasons. Please contact support. (ERR_CODE_019)':
            'لا يمكن تسجيل الدخول أو إنشاء الحساب لأسباب أمنية. يرجى الاتصال بالدعم.',

          // Email Password Translations
          EMAIL_PASSWORD_EMAIL_LABEL: 'البريد الإلكتروني',
          EMAIL_PASSWORD_EMAIL_PLACEHOLDER: 'عنوان البريد الإلكتروني',
          EMAIL_PASSWORD_PASSWORD_LABEL: 'كلمة المرور',
          EMAIL_PASSWORD_PASSWORD_PLACEHOLDER: 'كلمة المرور',
          EMAIL_PASSWORD_SIGN_IN_FORGOT_PW_LINK: 'نسيت كلمة المرور؟',
          EMAIL_PASSWORD_SIGN_IN_SUBMIT_BTN: 'تسجيل الدخول',
          EMAIL_PASSWORD_SIGN_IN_WRONG_CREDENTIALS_ERROR: 'تركيبة البريد الإلكتروني وكلمة المرور غير صحيحة',
          EMAIL_PASSWORD_SIGN_UP_SUBMIT_BTN: 'إنشاء حساب',
          EMAIL_PASSWORD_EMAIL_ALREADY_EXISTS: 'هذا البريد الإلكتروني موجود بالفعل. يرجى تسجيل الدخول بدلاً من ذلك',
          EMAIL_PASSWORD_RESET_HEADER_TITLE: 'إعادة تعيين كلمة المرور',
          EMAIL_PASSWORD_RESET_HEADER_SUBTITLE: 'سنرسل لك بريدًا إلكترونيًا لإعادة تعيين كلمة المرور الخاصة بك',
          EMAIL_PASSWORD_RESET_SEND_FALLBACK_EMAIL: 'حسابك',
          EMAIL_PASSWORD_RESET_SEND_BEFORE_EMAIL: 'تم إرسال بريد إلكتروني لإعادة تعيين كلمة المرور إلى ',
          EMAIL_PASSWORD_RESET_SEND_AFTER_EMAIL: '، إذا كان موجودًا في نظامنا.',
          EMAIL_PASSWORD_RESET_RESEND_LINK: 'إعادة إرسال أو تغيير البريد الإلكتروني',
          EMAIL_PASSWORD_RESET_SEND_BTN: 'أرسل لي البريد الإلكتروني',
          EMAIL_PASSWORD_RESET_SIGN_IN_LINK: 'تسجيل الدخول',
          EMAIL_PASSWORD_RESET_SUBMIT_PW_SUCCESS_HEADER_TITLE: 'نجاح!',
          EMAIL_PASSWORD_RESET_SUBMIT_PW_SUCCESS_DESC: 'تم تحديث كلمة المرور الخاصة بك بنجاح',
          EMAIL_PASSWORD_RESET_SUBMIT_PW_SUCCESS_SIGN_IN_BTN: 'تسجيل الدخول',
          EMAIL_PASSWORD_NEW_PASSWORD_LABEL: 'كلمة المرور الجديدة',
          EMAIL_PASSWORD_NEW_PASSWORD_PLACEHOLDER: 'كلمة المرور الجديدة',
          EMAIL_PASSWORD_CONFIRM_PASSWORD_LABEL: 'تأكيد كلمة المرور',
          EMAIL_PASSWORD_CONFIRM_PASSWORD_PLACEHOLDER: 'أكد كلمة المرور الخاصة بك',
          EMAIL_PASSWORD_RESET_SUBMIT_PW_HEADER_TITLE: 'تغيير كلمة المرور الخاصة بك',
          EMAIL_PASSWORD_RESET_SUBMIT_PW_HEADER_SUBTITLE: 'أدخل كلمة مرور جديدة أدناه لتغيير كلمة المرور الخاصة بك',
          EMAIL_PASSWORD_RESET_SUBMIT_PW_CHANGE_PW_BTN: 'تغيير كلمة المرور',
          EMAIL_PASSWORD_RESET_PASSWORD_INVALID_TOKEN_ERROR: 'رمز إعادة تعيين كلمة المرور غير صالح',
          ERROR_EMAIL_NON_STRING: 'يجب أن يكون البريد الإلكتروني من نوع نصي',
          ERROR_EMAIL_INVALID: 'البريد الإلكتروني غير صالح',
          ERROR_PASSWORD_NON_STRING: 'يجب أن تكون كلمة المرور من نوع نصي',
          ERROR_PASSWORD_TOO_SHORT: 'يجب أن تحتوي كلمة المرور على 8 أحرف على الأقل، بما في ذلك رقم',
          ERROR_PASSWORD_TOO_LONG: 'يجب أن يكون طول كلمة المرور أقل من 100 حرف',
          ERROR_PASSWORD_NO_ALPHA: 'يجب أن تحتوي كلمة المرور على حرف أبجدي واحد على الأقل',
          ERROR_PASSWORD_NO_NUM: 'يجب أن تحتوي كلمة المرور على رقم واحد على الأقل',
          ERROR_CONFIRM_PASSWORD_NO_MATCH: 'كلمة تأكيد كلمة المرور غير متطابقة',
          ERROR_NON_OPTIONAL: 'هذا الحقل غير اختياري',
          'This email already exists. Please sign in instead.':
            'هذا البريد الإلكتروني موجود بالفعل. يرجى تسجيل الدخول بدلاً من ذلك',
          'Field is not optional': 'هذا الحقل غير اختياري',
          'Password must contain at least 8 characters, including a number':
            'يجب أن تحتوي كلمة المرور على 8 أحرف على الأقل، بما في ذلك رقم',
          "Password's length must be lesser than 100 characters": 'يجب أن يكون طول كلمة المرور أقل من 100 حرف',
          'Password must contain at least one alphabet': 'يجب أن تحتوي كلمة المرور على حرف أبجدي واحد على الأقل',
          'Password must contain at least one number': 'يجب أن تحتوي كلمة المرور على رقم واحد على الأقل',
          'Email is invalid': 'البريد الإلكتروني غير صالح',
          'Reset password link was not created because of account take over risk. Please contact support. (ERR_CODE_001)':
            'لم يتم إنشاء رابط إعادة تعيين كلمة المرور بسبب خطر استيلاء على الحساب. يرجى الاتصال بالدعم',
          'Cannot sign up due to security reasons. Please try logging in, use a different login method or contact support. (ERR_CODE_007)':
            'لا يمكن إنشاء حساب لأسباب أمنية. يرجى محاولة تسجيل الدخول، استخدام طريقة تسجيل دخول مختلفة أو الاتصال بالدعم',
          'Cannot sign in due to security reasons. Please try resetting your password, use a different login method or contact support. (ERR_CODE_008)':
            'لا يمكن تسجيل الدخول لأسباب أمنية. يرجى محاولة إعادة تعيين كلمة المرور، استخدام طريقة تسجيل دخول مختلفة أو الاتصال بالدعم',
          'Cannot sign in / up due to security reasons. Please contact support. (ERR_CODE_009)':
            'لا يمكن تسجيل الدخول / إنشاء الحساب لأسباب أمنية. يرجى الاتصال بالدعم',
          'Cannot sign in / up due to security reasons. Please contact support. (ERR_CODE_010)':
            'لا يمكن تسجيل الدخول / إنشاء الحساب لأسباب أمنية. يرجى الاتصال بالدعم',
          'Cannot sign in / up due to security reasons. Please contact support. (ERR_CODE_011)':
            'لا يمكن تسجيل الدخول / إنشاء الحساب لأسباب أمنية. يرجى الاتصال بالدعم',
          'Cannot sign in / up due to security reasons. Please contact support. (ERR_CODE_012)':
            'لا يمكن تسجيل الدخول / إنشاء الحساب لأسباب أمنية. يرجى الاتصال بالدعم',
          'Cannot sign in / up due to security reasons. Please contact support. (ERR_CODE_013)':
            'لا يمكن تسجيل الدخول / إنشاء الحساب لأسباب أمنية. يرجى الاتصال بالدعم',
          'Cannot sign in / up due to security reasons. Please contact support. (ERR_CODE_014)':
            'لا يمكن تسجيل الدخول / إنشاء الحساب لأسباب أمنية. يرجى الاتصال بالدعم',
          'Cannot sign in / up due to security reasons. Please contact support. (ERR_CODE_015)':
            'لا يمكن تسجيل الدخول / إنشاء الحساب لأسباب أمنية. يرجى الاتصال بالدعم',
          'Cannot sign in / up due to security reasons. Please contact support. (ERR_CODE_016)':
            'لا يمكن تسجيل الدخول / إنشاء الحساب لأسباب أمنية. يرجى الاتصال بالدعم',
          'First Name': `الاسم الاول`,
          'Last Name': `الاسم الاخير`,
          // ... Add other translation keys as needed.
        },
      };
    } else {
      translations = {};
    }
    return translations;
  }

  authInfoStyle(language: 'en' | 'ar' = 'ar') {
    let style: string = '';
    if (language === 'en') {
      style = `
        /* Hide the SuperTokens branding element */
        [data-supertokens~=superTokensBranding] {
          display: none;
        }

        /* Customize button styling */
        [data-supertokens="button"] {
          background-color: rgb(255 178 13);
          border-color: rgb(255 178 13);
        }    
        [data-supertokens~="link"]{
          color: rgb(255 178 13);
        }

        /* Customize container style */
        [data-supertokens~="container"]{
          box-shadow: none;
          border: 1px solid #e7e7e7;
          font-family: "Inter", sans-serif;
        }

        [data-supertokens~="input"]{
          text-align: left;
        }

        [data-supertokens~="formLabelWithLinkWrapper"]{
          direction: ltr;
        }

        [data-supertokens~="button"]{
          font-family: "Inter", sans-serif;
          color: #333333;
        }

        [data-supertokens~="linkButton"]{
          font-family: "Inter", sans-serif;
        }

        [data-supertokens~="button"][data-supertokens~="providerButton"]:hover{
          background-color: #f1f5f9;
          border-color: #ddd;
        }

        [data-supertokens~="button"]:hover{
          background-color: #faa104;
          border-color: #faa104;
        }
        `;
    } else {
      style = `
        /* Hide the SuperTokens branding element */
        [data-supertokens~=superTokensBranding] {
          display: none;
        }

        /* Customize button styling */
        [data-supertokens="button"] {
          background-color: rgb(255 178 13);
          border-color: rgb(255 178 13);
        }    
        [data-supertokens~="link"]{
          color: rgb(255 178 13);
        }

        /* Customize container style */
        [data-supertokens~="container"]{
          box-shadow: none;
          border: 1px solid #e7e7e7;
          font-family: Tajawal, sans-serif;
        }

        [data-supertokens~="input"]{
          text-align: right;
        }

        [data-supertokens~="formLabelWithLinkWrapper"]{
          direction: rtl;
        }

        [data-supertokens~="label"]{
          text-align: right;
        }

        [data-supertokens~="providerButtonText"]{
          direction: rtl;
        }

        [data-supertokens~="button"]{
          font-family: Tajawal, sans-serif;
          color: #333333;
          font-weight: 500;
        }

        [data-supertokens~="linkButton"]{
          font-family: "Inter", sans-serif;
        }
        
        [data-supertokens~="button"][data-supertokens~="providerButton"]:hover{
          background-color: #f1f5f9;
          border-color: #ddd;
        }

        [data-supertokens~="button"]:hover{
          background-color: #faa104;
          border-color: #faa104;
        }
        `;
    }
    return style;
  }

  websiteBasePath(language: 'en' | 'ar' = 'ar', isLocalDev: boolean) {
    if (isLocalDev) return '/auth';
    return language === 'en' ? '/en/auth' : '/auth';
  }

  // Get geolocation info
  private country: string = '';
  async initGeolocation(): Promise<void> {
    // const geo = await firstValueFrom(this.geoService.geolocation);
    this.geoService.getGeolocationInfo().subscribe((geo) => {
      this.country = geo.country || '';
    });
  }
  getCountryHeader(): string {
    return this.country;
  }
}
